import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { MainApi } from '@mark/web-contracts';
import { catchError, map, Observable, of, withLatestFrom } from 'rxjs';

import { AccountGrpcContractService } from '../api/grpc/services/account.service';
import { RouteString } from '../enums/route-string';
import { SELECTED_LANG } from '../providers/selected-lang.provider';
import { ErrorService } from './error.service';
import { SSRService } from './ssr.service';

@Injectable({ providedIn: 'root' })
export class AccountRedirectionService {
  private readonly _accountGrpcContractService = inject(AccountGrpcContractService);
  private readonly _errorService = inject(ErrorService);
  private readonly _router = inject(Router);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _locale$ = inject(SELECTED_LANG);

  constructor(private readonly _SSRService: SSRService) {}

  getStatus(): Observable<boolean> {
    return this._accountGrpcContractService.getStatus().pipe(
      withLatestFrom(this._locale$),
      map(([{ BioHackingStatus }, locale]) => {
        let res: boolean = false;
        let redirect: Array<string> = [];
        switch (BioHackingStatus!.StatusWpc) {
          case MainApi.BioHackingUserStatusWpc.BioHackingUserStatusWpc_None:
          case MainApi.BioHackingUserStatusWpc.BioHackingUserStatusWpc_SendRequestToCreatePlan:
          case MainApi.BioHackingUserStatusWpc.BioHackingUserStatusWpc_PlanIsReady:
            res = true;
            break;
          case MainApi.BioHackingUserStatusWpc.BioHackingUserStatusWpc_SurveyCompleted:
            res = false;
            redirect = ['/', locale, RouteString.PLAN];
            break;
          case MainApi.BioHackingUserStatusWpc.BioHackingUserStatusWpc_CountDownToStartPlan:
          case MainApi.BioHackingUserStatusWpc.BioHackingUserStatusWpc_PlanCanceled:
          case MainApi.BioHackingUserStatusWpc.BioHackingUserStatusWpc_PlanCompleted:
          case MainApi.BioHackingUserStatusWpc.BioHackingUserStatusWpc_PlanInProgressWithSelectedSupplements:
          case MainApi.BioHackingUserStatusWpc.BioHackingUserStatusWpc_PlanInProgress:
          case MainApi.BioHackingUserStatusWpc.BioHackingUserStatusWpc_SurveyCompletedAfterPlan:
            res = false;
            redirect = ['/', locale, RouteString.FINAL];
            break;
          default:
            break;
        }
        return { canActivate: res, redirect };
      }),
      map(({ canActivate, redirect }) => {
        if (!canActivate) {
          this._router.navigate(redirect, {
            relativeTo: this._activatedRoute,
            queryParamsHandling: 'merge',
          });
        }
        return canActivate;
      }),
      catchError((response) => {
        return this._errorService.handleApiError(response, 'AccountGrpcContractClient.GetStatus', of(false));
      }),
    );
  }
}
